import {
  IOfferingRes,
  IQuoteOfferingReqSchema,
  IQuoteOfferingRespSchema,
  IQuoteRespSchema,
  OfferingTypesEnum,
  RateBillingFrequencyEnum,
} from '@monetize/types/app';
import { addDays } from 'date-fns/addDays';
import { addMonths } from 'date-fns/addMonths';
import { parseISO } from 'date-fns/parseISO';
import { useEffect, useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import {
  DatePicker,
  DatePickerProps,
} from '../../../../../components/Monetize/DatePicker/DatePicker';
import { hasUsageProducts } from './quoteOfferingUtils';

function computeScheduledMinDate(
  priorStartDate: string | undefined | null,
  contractStartDate: string,
  monthInterval: number,
): Date {
  return priorStartDate
    ? addMonths(parseISO(priorStartDate), monthInterval)
    : parseISO(contractStartDate);
}

function computeBaseDate(
  billingAnchorDay: number | undefined | null,
  contextDate: Date | undefined,
  contractStartDate: string,
): Date {
  if (billingAnchorDay && contextDate) {
    return new Date(
      contextDate.getFullYear(),
      contextDate.getMonth(),
      billingAnchorDay,
    );
  }
  return parseISO(contractStartDate);
}

interface QuoteOfferingScheduledEffectiveDateProps {
  isOfferingLoading: boolean;
  quote: IQuoteRespSchema;
  control: Control<IQuoteOfferingReqSchema>;
  priorScheduledQuoteOffering?: IQuoteOfferingRespSchema | null;
  nextScheduledQuoteOffering?: IQuoteOfferingRespSchema | null;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  isChildOffering?: boolean;
  onChange?: (date: string) => void;
  offering: IOfferingRes;
  monthInterval: number;
  parentQuoteOffering?: IQuoteOfferingRespSchema | null;
  isUsingDelayedBilling?: boolean;
  isAmendment?: boolean;
}

export const QuoteOfferingScheduledEffectiveDate = ({
  isOfferingLoading,
  control,
  quote,
  priorScheduledQuoteOffering,
  nextScheduledQuoteOffering,
  isReadOnly,
  isDisabled = false,
  isChildOffering = false,
  onChange: onChangeProp,
  offering,
  monthInterval,
  parentQuoteOffering,
  isUsingDelayedBilling,
  isAmendment,
}: QuoteOfferingScheduledEffectiveDateProps) => {
  const [minDate, setMinDate] = useState<Date>();
  const [maxDate, setMaxDate] = useState<Date>();
  const isMinCommit = offering.type === OfferingTypesEnum.MIN_COMMIT;

  const hasUsageProduct = hasUsageProducts(parentQuoteOffering, offering);

  const datepickerOptions: Partial<DatePickerProps> = {
    minDate,
    maxDate,
  };

  // Restrict mode based on use-case
  if (isMinCommit) {
    datepickerOptions.mode =
      parentQuoteOffering?.billingFrequency ===
      RateBillingFrequencyEnum.ANNUALLY
        ? 'YEAR'
        : 'MONTH';
    datepickerOptions.interval =
      parentQuoteOffering?.billingFrequency ===
      RateBillingFrequencyEnum.ANNUALLY
        ? 1
        : (monthInterval as 1 | 3 | 6); // This will be only 1 or 3 or 6
  } else if (hasUsageProduct && isChildOffering) {
    datepickerOptions.mode = 'MONTH';
    datepickerOptions.interval = 1;

    datepickerOptions.minDate = computeScheduledMinDate(
      priorScheduledQuoteOffering?.startDate,
      quote.contractStartDate,
      monthInterval,
    );
    datepickerOptions.baseDate = computeBaseDate(
      quote?.billingAnchor?.day,
      minDate,
      quote.contractStartDate,
    );
  }

  if (isUsingDelayedBilling) {
    datepickerOptions.mode = 'YEAR';
    datepickerOptions.interval = 1;
  }

  if (isMinCommit || isUsingDelayedBilling) {
    // Last frequency period cannot be selected
    datepickerOptions.minDate = computeScheduledMinDate(
      priorScheduledQuoteOffering?.startDate,
      quote.contractStartDate,
      monthInterval,
    );
    datepickerOptions.maxDate = addDays(
      addMonths(
        parseISO(
          nextScheduledQuoteOffering?.startDate || quote.contractEndDate,
        ),
        -1 * monthInterval,
      ),
      1,
    );
    datepickerOptions.baseDate = computeBaseDate(
      isMinCommit ? quote?.billingAnchor?.day : undefined,
      minDate,
      quote.contractStartDate,
    );
    datepickerOptions.anchorMonth =
      parseISO(quote.contractStartDate).getMonth() + 1;
  }

  useEffect(() => {
    const isOneTimeOffering =
      parentQuoteOffering?.billingFrequency ===
      RateBillingFrequencyEnum.ONETIME;

    if (isOneTimeOffering) {
      setMinDate(parseISO(quote.contractStartDate));
      setMaxDate(parseISO(quote.contractEndDate));
    } else {
      let minDateStr = quote.contractStartDate;
      const maxDateStr = parentQuoteOffering?.endDate || quote.contractEndDate;

      if (priorScheduledQuoteOffering?.startDate) {
        minDateStr = priorScheduledQuoteOffering.startDate;
      }

      setMinDate(
        isChildOffering
          ? addDays(parseISO(minDateStr), 1)
          : parseISO(minDateStr),
      );
      setMaxDate(parseISO(maxDateStr));
    }
  }, [
    quote?.type,
    priorScheduledQuoteOffering?.startDate,
    nextScheduledQuoteOffering?.endDate,
    quote.contractStartDate,
    quote.contractEndDate,
    isChildOffering,
    parentQuoteOffering,
  ]);

  const defaultDate = isAmendment ? new Date() : quote.contractStartDate;

  return (
    <Controller
      control={control}
      name={isChildOffering ? 'schedule.startDate' : 'startDate'}
      render={({ field: { onChange, value, ...rest } }) => (
        <DatePicker
          {...rest}
          value={!isChildOffering && !value ? defaultDate : value}
          {...datepickerOptions}
          onChange={(date) => {
            onChange(date);
            onChangeProp && date && onChangeProp(date);
          }}
          isReadOnly={isReadOnly}
          isDisabled={isOfferingLoading || isDisabled}
          variant={isReadOnly ? 'readonly' : 'outline'}
        />
      )}
    />
  );
};
